/**
 * Web ClipboardCapabilities — navigator.clipboard read/write (page gesture)
 * + broadcastClip fan-out. SECURITY: never logs clip content.
 */
import type { ClipboardCapabilities } from "@reflecto/client-ui";
import { logWarn } from "../lib/log";
import { androidPeerLabel, broadcastClip } from "../services/clipboard";
import { webStorage } from "./storage.web";

export function createWebClipboardCapabilities(): ClipboardCapabilities {
  return {
    readText: () => navigator.clipboard.readText(),
    writeText: (text) => navigator.clipboard.writeText(text),
    async broadcast(content, { sensitive }) {
      try {
        const { token, secretKey, selfDeviceName, androidDeviceName } = await webStorage.get(
          "token",
          "secretKey",
          "selfDeviceName",
          "androidDeviceName"
        );
        // Explicit guard so the adapter is self-documenting + deterministic
        // (broadcastClip also throws this downstream, but fail here first).
        if (!token || !secretKey) throw new Error("not_paired");
        return await broadcastClip(token, secretKey, content, {
          originLabel: selfDeviceName,
          sensitive,
          // Recipient summary for the outbound clip-history record (C4).
          // MVP: the web app's only partner is the paired phone — its name
          // is exactly "the recipient summary the UI already shows" (the
          // HeaderBar's "Paired with X" line). `androidPeerLabel` is the
          // single shared derivation with `ShareTargetPage`'s equivalent
          // call (nitpick, round 3) — see its doc comment.
          peerLabel: androidPeerLabel(androidDeviceName),
        });
      } catch (err) {
        // Tag only (never the clip content); re-throw so ClipboardScreen surfaces its result line.
        // Skip the self-thrown `not_paired` guard — it's the normal unpaired case,
        // not a fanout failure, so logging it would be misleading.
        if ((err as Error).message !== "not_paired") {
          logWarn("clipboard", "clip_broadcast_failed", err);
        }
        throw err;
      }
    },
  };
}
